// Pure logic for the embroidery cart rules, shared by the in-house cart (assets/mnl-cart.js):
// "embroidery qty <= garment qty" cap counted PER JOB like the server, zone resync, the
// first-line-free offer badge, and the decision behind every +/-/remove click (planLineAction).
// Zero DOM, zero network: each `item` is a cart line { key, product_id, variant_id, quantity,
// properties }. Must load BEFORE assets/mnl-cart.js.
// Self-check: node temp/test-embroidery-cart-core.js
(function () {
  // Total quantity per GARMENT (line WITHOUT _broderie_product_id), keyed by product_id.
  function garmentQtyMap(items) {
    var garmentQtyByProductId = {};
    items.forEach(function (item) {
      if (!item.properties || !item.properties._broderie_product_id) {
        garmentQtyByProductId[item.product_id] =
          (garmentQtyByProductId[item.product_id] || 0) + item.quantity;
      }
    });
    return garmentQtyByProductId;
  }

  // Groups embroidery lines PER JOB (_broderie_id), in cart order. A multi-zone embroidery
  // creates one cart line PER ZONE sharing the same _broderie_id, but represents only ONE
  // embroidered garment unit - must count once, not once per zone. Mirrors the server rule
  // (Validation Function cart_validations_generate_run.js's `seenJobs` dedup by broderieId, and
  // the Cart Transform's groupJobs) - the client must count the same way or it blocks the "+"
  // too early (observed bug: 2 jobs, one with 2 zones, on 3 garments = 3 lines counted as 3
  // instead of 2 units). quantity = the job's shared quantity (defensive max in case a
  // sequential grouped increment hasn't updated all zones yet; every WRITE is atomic since
  // 2026-09-17, so this max is now only the conservative input to the cap decision, while
  // computeQuantityCorrections normalizes desynced zones down to minQuantity).
  function broderieJobsByGarment(items) {
    var byGarment = {}; // garmentProductId -> [{ broderieId, quantity, lines[] }] (cart order)
    var index = {}; // garmentProductId -> { broderieId -> job }
    items.forEach(function (item) {
      var garmentGid = item.properties && item.properties._broderie_product_id;
      var broderieId = item.properties && item.properties._broderie_id;
      if (!garmentGid || !broderieId) return;
      var garmentProductId = garmentGid.split("/").pop();
      byGarment[garmentProductId] = byGarment[garmentProductId] || [];
      index[garmentProductId] = index[garmentProductId] || {};
      var job = index[garmentProductId][broderieId];
      if (!job) {
        job = {
          broderieId: broderieId,
          quantity: item.quantity,
          minQuantity: item.quantity,
          lines: [],
        };
        index[garmentProductId][broderieId] = job;
        byGarment[garmentProductId].push(job);
      }
      job.quantity = Math.max(job.quantity, item.quantity);
      job.minQuantity = Math.min(job.minQuantity, item.quantity);
      job.lines.push(item);
    });
    return byGarment;
  }

  // Sum of quantities per job (deduped by _broderie_id) for each garment - the total that must
  // stay <= garment quantity.
  function broderieJobQtyByGarment(items) {
    var jobs = broderieJobsByGarment(items);
    var totals = {};
    Object.keys(jobs).forEach(function (g) {
      totals[g] = jobs[g].reduce(function (sum, job) { return sum + job.quantity; }, 0);
    });
    return totals;
  }

  // Multiple embroidery jobs (distinct _broderie_id) can share the same garment (user feedback
  // 2026-07-16) - the cap applies to the SUM of quantities PER JOB, allocated in cart order.
  // Corrects all lines (zones) of a job together to the same quantity, to never break the
  // invariant "every zone of a job has the same quantity". Returns a list of { item, quantity }
  // (item = the cart line to re-cap).
  function computeQuantityCorrections(items) {
    var garmentQtyByProductId = garmentQtyMap(items);
    var jobsByGarment = broderieJobsByGarment(items);

    var corrections = [];
    Object.keys(jobsByGarment).forEach(function (garmentProductId) {
      var remaining = garmentQtyByProductId[garmentProductId] || 0;
      jobsByGarment[garmentProductId].forEach(function (job) {
        // Target = the job's LOWEST zone quantity, not its highest. A multi-line cart write that
        // only partly landed leaves the zones of one job disagreeing (2026-09-17: the 2nd of two
        // chained writes from the former third-party cart came back 400 "Pas d'identifiant ou de paramètre de ligne valide",
        // zone 1 updated, zone 2 untouched). The per-job MAX hid that state from every guard: max
        // <= garment qty means "nothing to correct" here, the Validation Function counts one zone
        // per job (seenJobs), and the grouped card only renders the primary line's quantity - so
        // the cart reached checkout with one zone billed at the wrong quantity. Normalizing DOWN
        // is the safe direction (never bill a zone the customer didn't ask for) and matches the
        // "-" they clicked. maxedGarmentProductIds keeps using the max, on purpose: greying "+"
        // one step early is harmless, letting the cap be exceeded is not.
        var allowed = Math.min(job.minQuantity, Math.max(remaining, 0));
        remaining -= allowed;
        job.lines.forEach(function (line) {
          if (line.quantity !== allowed) corrections.push({ item: line, quantity: allowed });
        });
      });
    });
    return corrections;
  }

  // Garments (garmentProductId) whose TOTAL embroidery (per job) reaches/exceeds the garment
  // quantity -> all their embroidery lines are "at the cap". Shared by both UIs: the former third-party cart derived
  // which _broderie_id to grey out, /cart the per-line "+" buttons. (Counts per job, not per
  // line - the fix for the /cart counting bug.)
  function maxedGarmentProductIds(items) {
    var garmentQty = garmentQtyMap(items);
    var broderieQty = broderieJobQtyByGarment(items);
    var maxed = {};
    Object.keys(broderieQty).forEach(function (garmentProductId) {
      if (broderieQty[garmentProductId] >= (garmentQty[garmentProductId] || 0)) {
        maxed[garmentProductId] = true;
      }
    });
    return maxed;
  }

  // --- "First line free" offer: which cart lines ACTUALLY carry the discount -----------------
  // The Cart Transform (cart_transform_run.js) applies the discount via `fixedPricePerUnit`,
  // which OVERWRITES the line price with no original != final in the cart (verified live:
  // original_price === final_price === discounted price on the offered line, so a third-party cart shows no
  // strikethrough). The "gift first line free" badge can't be inferred from the displayed
  // price - this replays the server rule. EXACT mirror of cartTransformRun#offerActiveNow +
  // `primary`:
  //   offer applied  ==  offer.active AND (sum of _offer_eligible garment qty) >= offer.threshold
  //   discounted line == the 1st TEXT zone (coeur/centre) of EACH job, in cart order.
  // The Cart Transform remains the sole authority on PRICE; this only drives the badge display.
  // Any divergence would show "offered" on a line billed full price (below threshold) - hence
  // the strict mirror of eligible-garment counting and per-job counting for the discounted line.
  // offer = { active: bool, threshold: number } (set by theme.liquid from the metaobject).
  var OFFER_TEXT_ZONES = { coeur: true, centre: true };
  function emplacementOf(item) {
    return (item.properties && item.properties.Emplacement) || null;
  }

  // Rule #2 (user feedback 2026-07-27): the threshold counts the quantity of GARMENTS eligible
  // for the offer (property _offer_eligible, set by product-form1.liquid from
  // product.metafields.custom.offre_broderie), embroidered or not - mirrors
  // cart_transform_run.js#eligibleGarmentQty. ponytail: this counter only sees lines added via
  // that form (a third-party cart/Buy Button/POS quick-add doesn't set this property) - the badge can stay
  // wrongly hidden for those channels; the Function reads the product metafield directly and
  // stays exact on the billed price. Upgrade: expose eligibility via an app proxy endpoint if
  // this gap becomes a problem in practice.
  function eligibleGarmentQty(items) {
    var total = 0;
    items.forEach(function (item) {
      var props = item.properties || {};
      if (props._broderie_product_id) return; // embroidery/engraving line, not a garment
      if (props._offer_eligible === 'true') total += item.quantity;
    });
    return total;
  }
  // Garments eligible for the offer (by product_id) - used to discount ONLY jobs linked to a
  // garment that is itself eligible, not the whole cart once the global threshold is reached
  // elsewhere (user feedback 2026-07-29: a job embroidered on a non-offer garment must never be
  // discounted, even if other garments in the cart reach the threshold).
  function garmentEligibilityMap(items) {
    var eligible = {};
    items.forEach(function (item) {
      var props = item.properties || {};
      if (props._broderie_product_id) return; // embroidery/engraving line, not a garment
      if (props._offer_eligible === 'true') eligible[item.product_id] = true;
    });
    return eligible;
  }

  // KEEP IN SYNC with apps/embroidery-app/extensions/embroidery-cart-transform/src/cart_transform_run.js
  // (repo manelli-shopify-applications) - per-brand scope rule:
  //   scopeOf(vendor) = brandThresholds has normalized(vendor) ? normalized(vendor) : "*"
  //   a job is offered iff eligible qty of ITS scope >= threshold of that scope ("*" = global threshold).
  // offer.brandThresholds is optional ({ "manelli": 2 }); missing = global rule only.
  function normalizeVendor(v) { return String(v || '').trim().toLowerCase(); }

  function offeredLineKeys(items, offer) {
    var offered = {};
    if (!offer || !offer.active) return offered;
    var threshold = offer.threshold > 0 ? offer.threshold : 3;
    var rules = offer.brandThresholds || {};
    function scopeOf(vendor) {
      var v = normalizeVendor(vendor);
      return Object.prototype.hasOwnProperty.call(rules, v) ? v : '*';
    }
    function thresholdOf(scope) { return scope === '*' ? threshold : rules[scope]; }

    var garmentEligible = garmentEligibilityMap(items);
    var qtyByScope = {};
    var scopeByProduct = {};
    items.forEach(function (item) {
      var props = item.properties || {};
      if (props._broderie_product_id || props._offer_eligible !== 'true') return;
      var scope = scopeOf(item.vendor);
      scopeByProduct[item.product_id] = scope;
      qtyByScope[scope] = (qtyByScope[scope] || 0) + item.quantity;
    });

    // Groups by job (_broderie_id), in cart order - a multi-zone embroidery shares a single
    // _broderie_id = 1 job = 1 unit (same dedup as broderieJobsByGarment / the server).
    var jobs = [];
    var byId = {};
    items.forEach(function (item) {
      var jobId = item.properties && item.properties._broderie_id;
      if (!jobId) return;
      var job = byId[jobId];
      if (!job) { job = { quantity: item.quantity, lines: [] }; byId[jobId] = job; jobs.push(job); }
      job.quantity = Math.max(job.quantity, item.quantity);
      job.lines.push(item);
    });

    // Discounted line = 1st TEXT zone (coeur/centre) of each job, ONLY if the garment this job
    // is linked to (_broderie_product_id) is itself offer-eligible - exact mirror of
    // cart_transform_run.js#garmentEligibilityMap. Fallback: no garment found = no discount
    // (orphan job, safe default).
    jobs.forEach(function (job) {
      var garmentGid = job.lines[0].properties && job.lines[0].properties._broderie_product_id;
      var garmentProductId = garmentGid ? garmentGid.split('/').pop() : null;
      if (!garmentProductId || !garmentEligible[garmentProductId]) return;
      var scope = scopeByProduct[garmentProductId];
      if ((qtyByScope[scope] || 0) < thresholdOf(scope)) return;
      for (var i = 0; i < job.lines.length; i++) {
        if (OFFER_TEXT_ZONES[emplacementOf(job.lines[i])] && job.lines[i].key) {
          offered[job.lines[i].key] = true;
          break;
        }
      }
    });
    return offered;
  }

  // Applied promo codes vs the embroidery offer, same decision as the checkout guard (repo
  // manelli-shopify-applications, embroidery-discount-guard offer-guard-core.js: normalizeCode,
  // isCodeExempt, isGravureOfferteActive, nonExemptAppliedCodes) - KEEP IN SYNC.
  //   offer applies  == offeredLineKeys non-empty OR a free-engraving line
  //   conflict       == offer applies AND a non-exempt code is applied AND the offer isn't waived
  //   waivedWithCode == waived AND a non-exempt code is still applied
  //   staleWaiver    == waived AND no non-exempt code left (checkout auto-reverts it, so do we)
  // offer = window.EmbroideryOffer (exemptCodes optional); waived = _embroidery_offer_waived === "true".
  function normalizeCode(code) { return String(code || '').trim().toLowerCase(); }

  function discountConflictState(items, offer, appliedCodes, waived) {
    var exempt = {};
    ((offer && offer.exemptCodes) || []).forEach(function (code) { exempt[normalizeCode(code)] = true; });
    var offerApplies = Object.keys(offeredLineKeys(items, offer)).length > 0 || items.some(function (item) {
      var props = item.properties || {};
      return props._broderie_type === 'gravure' && props._gravure_offerte === 'true';
    });
    var nonExemptCodes = [];
    var exemptAppliedCodes = [];
    (appliedCodes || []).forEach(function (code) {
      (exempt[normalizeCode(code)] ? exemptAppliedCodes : nonExemptCodes).push(code);
    });
    if (!offerApplies) nonExemptCodes = []; // no offer, nothing to conflict with (checkout parity)
    return {
      conflict: nonExemptCodes.length > 0 && !waived,
      waivedWithCode: !!waived && nonExemptCodes.length > 0,
      staleWaiver: !!waived && nonExemptCodes.length === 0,
      nonExemptCodes: nonExemptCodes,
      exemptAppliedCodes: exemptAppliedCodes,
    };
  }

  // Lines linked to THIS garment. _broderie_garment_variant_id (set by embroidery-app's
  // embroidery-cart-add.js) tells apart 2 sizes of the same product, which _broderie_product_id
  // can't (2026-07-17: jacket in M and XL, each with its own embroidery). Falls back to
  // product_id ONLY when no line of this product carries the variant property (cart added before
  // that property existed) - once any line uses it, 0 matches is a real answer, not a fallback.
  function linkedBroderieLinesFor(items, garmentItem) {
    var garmentGid = 'gid://shopify/Product/' + garmentItem.product_id;
    var linkedToProduct = items.filter(function (item) {
      return item.properties && item.properties._broderie_product_id === garmentGid;
    });
    var usesVariantLinking = linkedToProduct.some(function (item) {
      return !!item.properties._broderie_garment_variant_id;
    });
    if (!usesVariantLinking) return linkedToProduct;
    var variantId = String(garmentItem.variant_id || garmentItem.id);
    return linkedToProduct.filter(function (item) {
      return item.properties._broderie_garment_variant_id === variantId;
    });
  }

  // Garment going down to targetGarmentQty: nothing to do ("safe"), move its single linked job
  // down with it ("lockstep"), or refuse because 2+ distinct customizations are linked and we
  // never guess which one to reduce ("block"). Counts PER JOB, like the Validation Function.
  function decideGarmentDecrease(items, garmentItem, targetGarmentQty) {
    var jobs = {};
    linkedBroderieLinesFor(items, garmentItem).forEach(function (item) {
      var jobId = item.properties._broderie_id;
      (jobs[jobId] = jobs[jobId] || []).push(item);
    });
    var jobIds = Object.keys(jobs);
    var totalBroderieQty = jobIds.reduce(function (sum, id) { return sum + jobs[id][0].quantity; }, 0);
    if (totalBroderieQty <= targetGarmentQty) return { action: 'safe' };
    if (jobIds.length === 1) {
      return {
        action: 'lockstep',
        corrections: jobs[jobIds[0]].map(function (line) { return { item: line, quantity: targetGarmentQty }; }),
      };
    }
    return { action: 'block' };
  }

  // Quantity of the OTHER garment lines that cover the same embroidery as garmentItem: same
  // variant (a duplicate line - product-form1 adds _offer_eligible, quick add doesn't), or same
  // product in the legacy fallback where no linked line carries _broderie_garment_variant_id.
  function otherGarmentQty(items, garmentItem, excluded) {
    var gid = 'gid://shopify/Product/' + garmentItem.product_id;
    var byVariant = items.some(function (item) {
      return item.properties && item.properties._broderie_product_id === gid && !!item.properties._broderie_garment_variant_id;
    });
    return items.reduce(function (sum, item) {
      var isGarment = !item.properties || !item.properties._broderie_product_id;
      var same = item.product_id === garmentItem.product_id &&
        (!byVariant || String(item.variant_id) === String(garmentItem.variant_id));
      return isGarment && same && excluded.indexOf(item) === -1 ? sum + item.quantity : sum;
    }, 0);
  }

  // The whole decision behind one +/-/remove click in the cart, as ONE /cart/update.js payload
  // (line key -> quantity): a multi-line change lands entirely or not at all. `keys` are the cart
  // lines one displayed row drives - a garment line, or every zone of one embroidery job.
  // Garment rule: remaining = what still covers its embroidery after the click (other lines of the
  // same variant + target), then decideGarmentDecrease(remaining): 'safe' -> only the garment
  // moves; 'lockstep' -> its single job follows down to remaining; 'block' -> a "-" is refused,
  // while a complete removal (trash, or "-" at 1) is never refused: it zeroes the linked lines
  // only when nothing remains, otherwise leaves them to heal() (computeQuantityCorrections).
  function planLineAction(items, keys, action) {
    var lines = items.filter(function (item) { return keys.indexOf(item.key) !== -1; });
    if (!lines.length) return { blocked: 'missing' };
    var primary = lines[0];
    var isGarment = !primary.properties || !primary.properties._broderie_product_id;
    var updates = {};
    function setAll(quantity) { lines.forEach(function (line) { updates[line.key] = quantity; }); }
    var removing = action === 'remove' || (action === 'minus' && primary.quantity <= 1);

    if (removing && !isGarment) {
      setAll(0);
      return { updates: updates };
    }

    if (action === 'plus') {
      if (!isGarment) {
        var garmentProductId = primary.properties._broderie_product_id.split('/').pop();
        if (maxedGarmentProductIds(items)[garmentProductId]) return { blocked: 'max' };
      }
      setAll(primary.quantity + 1);
      return { updates: updates };
    }

    var target = removing ? 0 : primary.quantity - 1;
    if (!isGarment) {
      setAll(target);
      return { updates: updates };
    }
    var remaining = target + otherGarmentQty(items, primary, lines);
    var decision = decideGarmentDecrease(items, primary, remaining);
    if (decision.action === 'block') {
      if (!removing) return { blocked: 'garment-decrease' };
      if (remaining === 0) {
        linkedBroderieLinesFor(items, primary).forEach(function (line) { updates[line.key] = 0; });
      }
    }
    if (decision.action === 'lockstep') {
      decision.corrections.forEach(function (c) { updates[c.item.key] = c.quantity; });
    }
    setAll(target);
    return { updates: updates };
  }

  window.EmbroideryCartCore = {
    garmentQtyMap: garmentQtyMap,
    broderieJobsByGarment: broderieJobsByGarment,
    broderieJobQtyByGarment: broderieJobQtyByGarment,
    computeQuantityCorrections: computeQuantityCorrections,
    maxedGarmentProductIds: maxedGarmentProductIds,
    offeredLineKeys: offeredLineKeys,
    discountConflictState: discountConflictState,
    eligibleGarmentQty: eligibleGarmentQty,
    linkedBroderieLinesFor: linkedBroderieLinesFor,
    decideGarmentDecrease: decideGarmentDecrease,
    planLineAction: planLineAction,
  };
})();
